/** Device and capability detection for voice capture. */

const MOBILE_UA_RE = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

/** Desktop OS platforms — present when DevTools emulates a phone on Windows/Mac. */
const DESKTOP_PLATFORM_RE = /^(Win32|Win64|Windows|MacIntel|Macintosh|CrOS)$/i;

/**
 * True when the browser is running on a real phone/tablet OS, not DevTools emulation.
 * Chrome responsive mode sets a mobile userAgent but keeps platform as Win32/MacIntel.
 */
export const isRealMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;

  const uaSaysMobile = MOBILE_UA_RE.test(navigator.userAgent);
  const isTouchIpad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  if (!uaSaysMobile && !isTouchIpad) return false;

  const platform = navigator.platform || '';
  if (uaSaysMobile && DESKTOP_PLATFORM_RE.test(platform) && !isTouchIpad) {
    return false;
  }

  return true;
};

/** True for phones/tablets (user agent). Use for capture/transcription strategy. */
export const isMobileUserAgent = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    MOBILE_UA_RE.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

/** True for mobile layout or device — used for UI affordances. */
export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    isMobileUserAgent()
    || ('ontouchstart' in window && window.innerWidth <= 1024)
  );
};

export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

export const isStandalonePwa = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const supportsWebSpeechRecognition = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

export const supportsMediaRecorderCapture = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
};

const WEB_SPEECH_UNRELIABLE_KEY = 'voiceWebSpeechUnreliable';
const SERVER_TRANSCRIPTION_UNAVAILABLE_KEY = 'voiceServerTranscriptionUnavailable';

/**
 * Mobile browsers always use MediaRecorder + server Whisper transcription.
 * Web Speech is unreliable on iOS and produces endless restart loops on Android
 * (continuous=false sessions end and restart while the UI stays on "Listening…").
 * Desktop keeps Web Speech when available for lower latency.
 *
 * When server transcription is unavailable, mobile falls back to browser speech
 * on the next mic tap (see markServerTranscriptionUnavailable).
 */
export const shouldUseMediaRecorderFallback = (): boolean => {
  if (!supportsMediaRecorderCapture()) return false;
  // Real phones/tablets only — DevTools mobile emulation keeps Web Speech.
  if (isRealMobileDevice()) {
    if (isServerTranscriptionUnavailable() && supportsWebSpeechRecognition()) {
      return false;
    }
    return true;
  }
  if (!supportsWebSpeechRecognition()) return true;
  if (isWebSpeechMarkedUnreliable()) return true;
  return false;
};

/** Web Speech failed with a network/service error; prefer server Whisper this session. */
export const markWebSpeechUnreliable = (): void => {
  try {
    sessionStorage.setItem(WEB_SPEECH_UNRELIABLE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
};

export const isWebSpeechMarkedUnreliable = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(WEB_SPEECH_UNRELIABLE_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearWebSpeechUnreliable = (): void => {
  try {
    sessionStorage.removeItem(WEB_SPEECH_UNRELIABLE_KEY);
  } catch {
    /* ignore */
  }
};

/** Server Whisper failed — next mobile mic tap should use browser speech. */
export const markServerTranscriptionUnavailable = (): void => {
  try {
    sessionStorage.setItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
};

export const isServerTranscriptionUnavailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearServerTranscriptionUnavailable = (): void => {
  try {
    sessionStorage.removeItem(SERVER_TRANSCRIPTION_UNAVAILABLE_KEY);
  } catch {
    /* ignore */
  }
};

export const getPreferredMediaRecorderMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const types = isIOS()
    ? ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  return types.find((type) => MediaRecorder.isTypeSupported(type));
};
